import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../material/material.module';
import { EpmDecisionEvidence } from '../common/epm-decisions';
import { CURRENT_STATE_PROJECT_KEY, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { MODEL_CHANGE_LIMITS, ModelChangeAnswer } from '../common/model-changes';
import { Organisation, Project, PROJECT_NAME_MAX, PROJECT_NAME_MIN } from '../common/organisation';
import { AIModelChangeService, PreparedProject } from '../ai/model-changes/ai-model-change.service';
import { EpmFutureStateService, EpmFutureStateStep } from '../epm/epm-future-state.service';
import { EpmAppliedChange, EpmFutureStatePlan, PlannedChange } from '../epm/epm-future-state';
import { USER_ROLE } from '../common/user';
import { recommendationDecision } from './recommendation-decisions';
import { Recommendation } from './recommendations';

export interface FutureStateDialogData {
    organisation: Organisation;
    evidence: EpmDecisionEvidence;      // the EPM scorecard's, for what the overview looks at
    recommendations: Recommendation[];  // those chosen
    base?: Project;                     // the project looked at, which the future state builds on; current state if none
    canChangeWorkspace: boolean;        // the user can edit what's looked at, so the changes can be made to it directly
}

// what the dialog closes with: the project kept, if one was, or whether the workspace looked at was changed
export type FutureStateDialogResult = { project?: Project; open?: boolean; changed?: boolean } | undefined;

// where the changes are made: a new project, or the workspace looked at
type Target = 'project' | 'workspace';

type Step = 'describe' | 'working' | 'questions' | 'done' | 'review' | 'applied';

// the changes asked for in words, as they're listed with the recommendations'
export const YOUR_REQUEST = 'Your request';

const EXAMPLES = [
    'Add an AI agent that triages incoming support emails and drafts the replies',
    'Add a new CRM that call center operators use to enter customer details',
    'Add two people to Technical support',
];

// Generates a future state from the Overview: the recommendations chosen, carried out by AI as changes to the models
// (as the EPM scorecard does for its decisions), and anything else the user describes in words, such as a new system or
// an AI agent doing certain tasks (as AI model changes do). AI asks about anything unclear in the description. The
// changes are made in a new project, listed when it's done, and the user names the project or deletes it. Or they're
// made to the workspace looked at: listed first, and only made when the user applies them, so until then they can cancel
@Component({
    selector: 'app-future-state-dialog',
    standalone: true,
    imports: [MaterialModule, FormsModule, NgTemplateOutlet],
    template: `
        <h2 mat-dialog-title>Create a future state</h2>
        <mat-dialog-content class="content">
            @switch (step) {
            @case ('describe') {
                @if (data.canChangeWorkspace) {
                <mat-radio-group class="target" [(ngModel)]="target" aria-label="Where to make the changes">
                    <mat-radio-button value="project" data-testid="future-state-target-project">Create a new project</mat-radio-button>
                    <mat-radio-button value="workspace" data-testid="future-state-target-workspace">Change {{ workspaceName }}</mat-radio-button>
                </mat-radio-group>
                }
                @if (target === 'project') {
                <p class="lead">LINQ creates a new project {{ data.base ? 'as a copy of' : 'from' }} <strong>{{ data.base ? data.base.name : 'current state' }}</strong> and AI makes the changes in it, so you can see what they would do. You can delete it afterwards if you don't want it.</p>
                } @else {
                <p class="lead">AI works out the changes to <strong>{{ workspaceName }}</strong> and lists them. Nothing is changed until you apply them, and you can cancel at any point before that.</p>
                }
                @if (data.recommendations.length) {
                <h3>The recommendations you chose</h3>
                <ul class="chosen" data-testid="future-state-chosen">@for (r of data.recommendations; track r.key) {<li>{{ r.title }}</li>}</ul>
                }
                <h3>{{ data.recommendations.length ? 'Anything else to include' : 'What to include' }}</h3>
                <mat-form-field appearance="outline" class="full" subscriptSizing="dynamic">
                    <mat-label>Describe it for AI</mat-label>
                    <textarea matInput [(ngModel)]="description" [maxlength]="maxDescription" rows="4" placeholder="For example, a new system, or an AI agent that does certain tasks" data-testid="future-state-description"></textarea>
                    <mat-hint align="end">{{ description.length }} / {{ maxDescription }}</mat-hint>
                </mat-form-field>
                <div class="examples">@for (e of examples; track e) {<button type="button" class="example" (click)="description = e">{{ e }}</button>}</div>
            }
            @case ('working') {
                <div class="working" role="status"><mat-spinner diameter="22"></mat-spinner><span>{{ progressText }}</span></div>
            }
            @case ('questions') {
                <p class="lead">AI needs to know a little more about what you described before it {{ target === 'project' ? 'changes the project' : 'works out the changes' }}.</p>
                <!-- a question can be long, so it is shown in full above its answer rather than as the field's one-line label -->
                @for (q of questions; track $index) {
                <div class="question">
                    <p class="question-text" [id]="'future-state-question-' + $index" data-testid="future-state-question">@if (questions.length > 1) {<span class="question-number">{{ $index + 1 }}.</span> }{{ q.question }}</p>
                    <mat-form-field appearance="outline" class="full" subscriptSizing="dynamic">
                        <mat-label>Your answer</mat-label>
                        <textarea matInput [(ngModel)]="q.answer" [maxlength]="maxAnswer" rows="2" [attr.aria-describedby]="'future-state-question-' + $index" data-testid="future-state-answer"></textarea>
                    </mat-form-field>
                </div>
                }
            }
            @case ('done') {
                <p class="lead">{{ madeCount ? madeCount + (madeCount === 1 ? ' change was' : ' changes were') + ' made in the new project.' : 'No changes could be made, so the project is as it started.' }}
                    Name it, look at it to compare it with {{ data.base ? data.base.name : 'current state' }}, or delete it if you don't want it.</p>
                <ng-container [ngTemplateOutlet]="changeList"></ng-container>
                <mat-form-field appearance="outline" class="full" subscriptSizing="dynamic">
                    <mat-label>Project name</mat-label>
                    <input matInput [(ngModel)]="name" [maxlength]="maxName" data-testid="future-state-name">
                </mat-form-field>
            }
            @case ('review') {
                <p class="lead">@if (madeCount) { {{ madeCount }} {{ madeCount === 1 ? 'change' : 'changes' }} would be made to <strong>{{ workspaceName }}</strong>. Nothing has been changed yet: apply {{ madeCount === 1 ? 'it' : 'them' }}, or cancel to leave {{ workspaceName }} as it is.} @else {None of the changes could be made to <strong>{{ workspaceName }}</strong>, so there's nothing to apply.}</p>
                <ng-container [ngTemplateOutlet]="changeList"></ng-container>
                @if (readOnly.length) {<p class="error" role="alert">You can't edit {{ readOnly.join(', ') }}, so the changes can't be applied. Create a new project with them instead.</p>}
            }
            @case ('applied') {
                <p class="lead">{{ madeCount }} {{ madeCount === 1 ? 'change was' : 'changes were' }} made to <strong>{{ workspaceName }}</strong>.</p>
                <ng-container [ngTemplateOutlet]="changeList"></ng-container>
            }
            }
            @if (error) {<p class="error" role="alert">{{ error }}</p>}

            <ng-template #changeList>
                <div class="changes" data-testid="future-state-changes">
                    @for (g of groups; track g.decision) {
                    <section>
                        <h4>{{ g.decision }}</h4>
                        <ul>@for (c of g.changes; track $index) {
                            <li [class.not-made]="!c.applied"><mat-icon aria-hidden="true">{{ c.applied ? 'check_circle' : 'block' }}</mat-icon>
                                <span>{{ c.description }}@if (!c.applied && c.reason) {<span class="reason"> Not made: {{ c.reason }}.</span>}</span></li>
                        }@empty {<li class="not-made"><mat-icon aria-hidden="true">block</mat-icon><span>No change could be made for this.</span></li>}</ul>
                    </section>
                    }
                </div>
            </ng-template>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            @switch (step) {
            @case ('describe') {
                <button mat-button (click)="close()">Cancel</button>
                <button mat-flat-button color="accent" (click)="create()" [disabled]="!canCreate" data-testid="future-state-create">Create future state</button>
            }
            @case ('working') {
                @if (target === 'workspace' && !applying) {<button mat-button (click)="close()" data-testid="future-state-cancel">Cancel</button>}
            }
            @case ('questions') {
                @if (target === 'project') {<button mat-button (click)="discard()" [disabled]="busy">Delete project</button>}
                @else {<button mat-button (click)="close()" data-testid="future-state-cancel">Cancel</button>}
                <button mat-flat-button color="accent" (click)="answer()" [disabled]="!canAnswer || busy" data-testid="future-state-continue">Continue</button>
            }
            @case ('done') {
                <button mat-button (click)="discard()" [disabled]="busy" data-testid="future-state-discard">Delete project</button>
                <button mat-stroked-button color="accent" (click)="finish(false)" [disabled]="busy" data-testid="future-state-done">Done</button>
                <button mat-flat-button color="accent" (click)="finish(true)" [disabled]="busy" data-testid="future-state-open">Look at it</button>
            }
            @case ('review') {
                <button mat-button (click)="close()" data-testid="future-state-cancel">Cancel</button>
                <button mat-flat-button color="accent" (click)="applyToWorkspace()" [disabled]="!madeCount || readOnly.length > 0 || applyFailed" data-testid="future-state-apply">Apply to {{ workspaceName }}</button>
            }
            @case ('applied') {
                <button mat-flat-button color="accent" (click)="dialogRef.close({ changed: true })" data-testid="future-state-close">Close</button>
            }
            }
        </mat-dialog-actions>
    `,
    styles: [`
        .content { display: flex; flex-direction: column; gap: 10px; min-width: min(560px, calc(100vw - 80px)); }
        .target { display: flex; gap: 20px; flex-wrap: wrap; }
        .lead { margin: 0; color: var(--slate, #5b6172); font-size: 13.5px; line-height: 1.5; }
        h3 { margin: 6px 0 0; font-size: 14px; font-weight: 600; }
        h4 { margin: 8px 0 4px; font-size: 13px; font-weight: 600; }
        .chosen { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.6; }
        .full { width: 100%; }
        .question { display: flex; flex-direction: column; gap: 6px; }
        .question-text { margin: 0; font-size: 14px; line-height: 1.5; white-space: pre-line; overflow-wrap: anywhere; }
        .question-number { margin-right: 4px; font-weight: 600; }
        .examples { display: flex; flex-wrap: wrap; gap: 6px; }
        .example { padding: 3px 10px; border: 1px solid var(--line, #e7e9f0); border-radius: 12px; background: var(--paper, #fff); color: var(--teal-dark, #00706d); font: inherit; font-size: 12px; cursor: pointer; text-align: left; }
        .example:hover { background: var(--bg, #f4f5f9); }
        .working { display: flex; align-items: center; gap: 10px; padding: 18px 0; color: var(--slate, #5b6172); }
        .changes { max-height: 320px; overflow: auto; }
        .changes ul { list-style: none; margin: 0; padding: 0; }
        .changes li { display: flex; gap: 8px; align-items: flex-start; padding: 4px 0; font-size: 13px; line-height: 1.45; }
        .changes li mat-icon { flex: none; width: 18px; height: 18px; font-size: 18px; color: #2f9e5b; }
        .changes li.not-made { color: var(--slate, #5b6172); }
        .changes li.not-made mat-icon { color: var(--grey, #9aa0b0); }
        .reason { color: var(--grey, #9aa0b0); }
        .error { margin: 0; color: #b42318; font-size: 13px; }
    `],
})
export class FutureStateDialogComponent {
    readonly examples = EXAMPLES;
    readonly maxDescription = MODEL_CHANGE_LIMITS.description;
    readonly maxAnswer = MODEL_CHANGE_LIMITS.answer;
    readonly maxName = PROJECT_NAME_MAX;

    step: Step = 'describe';
    target: Target = 'project';
    description = '';
    questions: { question: string; answer: string }[] = [];
    changes: EpmAppliedChange[] = [];
    name = '';
    error = '';
    busy = false;
    progressText = '';
    applying = false;           // the changes are being saved to the workspace, so it's too late to cancel
    applyFailed = false;        // saving them failed, perhaps part way, so they aren't applied again
    readOnly: string[] = [];    // the models the changes would touch that the user can't edit

    private project?: Project;
    private cancelled = false;
    // the workspace's changes: AI's, to be planned against it as it is when they're applied, and those it couldn't make
    private pending: PlannedChange[] = [];
    private notMade: EpmAppliedChange[] = [];
    private prepared?: PreparedProject;
    private answers: ModelChangeAnswer[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) public data: FutureStateDialogData, public dialogRef: MatDialogRef<FutureStateDialogComponent, FutureStateDialogResult>,
        private futureStateService: EpmFutureStateService, private modelChangeService: AIModelChangeService) {
        dialogRef.disableClose = true;
    }

    static open(dialog: MatDialog, data: FutureStateDialogData) {
        return dialog.open<FutureStateDialogComponent, FutureStateDialogData, FutureStateDialogResult>(FutureStateDialogComponent, {
            data, width: '680px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 64px)', autoFocus: 'textarea',
        });
    }

    get workspaceName() {
        return this.data.base ? this.data.base.name : 'the current state';
    }

    // the workspace looked at, as the project the changes are made in
    private get workspace(): Project {
        return this.data.base || { _key: CURRENT_STATE_PROJECT_KEY, name: 'Current state' } as Project;
    }

    get canCreate() {
        return this.data.recommendations.length > 0 || this.description.trim().length > 0;
    }

    get canAnswer() {
        return this.questions.every(q => q.answer.trim().length > 0);
    }

    get madeCount() {
        return this.changes.filter(c => c.applied).length;
    }

    // the changes, by the recommendation they carry out, then what was described
    get groups(): { decision: string; changes: EpmAppliedChange[] }[] {
        const decisions = [...this.data.recommendations.map(r => r.title), ...(this.description.trim() ? [YOUR_REQUEST] : [])];
        const others = [...new Set(this.changes.map(c => c.decision || YOUR_REQUEST))].filter(d => !decisions.includes(d));
        return [...decisions, ...others].map(decision => ({ decision, changes: this.changes.filter(c => (c.decision || YOUR_REQUEST) === decision) }));
    }

    // makes the project, with the recommendations' changes, then asks AI about the description
    async create() {
        if (!this.canCreate) return;
        this.step = 'working';
        this.error = '';
        if (this.target === 'workspace') return this.proposeForWorkspace();
        try {
            if (this.data.recommendations.length) await this.generateFromRecommendations();
            else {
                this.progressText = this.data.base ? `Copying ${this.data.base.name}…` : 'Creating the project…';
                const request = this.description.trim();
                this.project = await this.futureStateService.createProject(this.data.organisation, `Future state: ${request.split(/[.\n]/)[0].trim()}`, `Future state: ${request}`, this.data.base);
            }
            this.name = this.project!.name;
            if (this.description.trim()) await this.ask();
            else this.step = 'done';
        } catch (err: any) {
            console.error('Unable to create the future state', err);
            this.error = (err?.message && !err?.status ? err.message : "The future state couldn't be created.") + ' Please try again.';
            await this.removeProject();
            this.step = 'describe';
        }
    }

    // the recommendations as decisions, carried out by AI in a new project. If none can be turned into changes the
    // described changes can still be made, in a new project without them
    private async generateFromRecommendations() {
        const decisions = this.data.recommendations.map(r => recommendationDecision(r, this.data.evidence));
        const text: Record<EpmFutureStateStep, string> = {
            planning: 'Working out the changes for the recommendations with AI…', creating: this.data.base ? `Copying ${this.data.base.name}…` : 'Creating the project…',
            applying: 'Changing the models…', scoring: 'Scoring the future state…',
        };
        try {
            const generated = await this.futureStateService.generate(this.data.organisation, this.data.evidence, decisions, step => this.progressText = text[step], this.data.base);
            this.project = generated.project;
            this.changes = generated.changes;
        } catch (err: any) {
            if (!this.description.trim()) throw err;
            this.changes = this.data.recommendations.map(r => ({ decision: r.title, description: r.title, applied: false, reason: "AI couldn't turn it into a change LINQ can make" }));
            this.progressText = this.data.base ? `Copying ${this.data.base.name}…` : 'Creating the project…';
            this.project = await this.futureStateService.createProject(this.data.organisation, `Future state: ${this.data.recommendations[0].title}`, `Future state for: ${this.data.recommendations.map(r => r.title).join('; ')}`, this.data.base);
        }
    }

    // the changes to the workspace: AI's for the recommendations, then for the description, listed before any are made
    private async proposeForWorkspace() {
        try {
            if (this.data.recommendations.length) {
                this.progressText = 'Working out the changes for the recommendations with AI…';
                const decisions = this.data.recommendations.map(r => recommendationDecision(r, this.data.evidence));
                try {
                    const proposed = await this.futureStateService.propose(this.data.organisation, this.data.evidence, decisions);
                    this.pending = proposed.usable;
                    this.notMade = proposed.rejected;
                } catch (err) {
                    if (!this.description.trim()) throw err;
                    this.notMade = this.data.recommendations.map(r => ({ decision: r.title, description: r.title, applied: false, reason: "AI couldn't turn it into a change LINQ can make" }));
                }
            }
            if (this.cancelled) return;
            if (this.description.trim()) await this.ask();
            else await this.planForWorkspace();
        } catch (err: any) {
            if (this.cancelled) return;
            console.error('Unable to work out the changes', err);
            this.error = (err?.message && !err?.status ? err.message : "The changes couldn't be worked out.") + ' Please try again.';
            this.pending = [];
            this.notMade = [];
            this.step = 'describe';
        }
    }

    // what the changes would do to the workspace as it is now, and whether the user can make them
    private async planForWorkspace() {
        this.progressText = 'Working out the changes to the models…';
        this.prepared ||= await this.modelChangeService.prepare(this.data.organisation, this.workspace);
        const plan = this.pending.length ? await this.modelChangeService.plan(this.data.organisation, this.workspace, this.prepared, this.pending) : undefined;
        if (this.cancelled) return;
        this.changes = [...(plan?.applied || []), ...this.notMade];
        this.readOnly = plan ? this.readOnlyIn(plan) : [];
        this.step = 'review';
    }

    // the parts of the workspace a plan changes that the user can only read
    private readOnlyIn(plan: EpmFutureStatePlan) {
        const organisation = this.data.organisation;
        const names = Object.keys(plan.changes).map(key => {
            if (key === ORGANISATION_MODEL_KEY) return organisation.role === USER_ROLE.READ ? 'the roles and systems' : undefined;
            const model = organisation.models?.find(m => m._key === key);
            return model?.role === USER_ROLE.READ ? model.name : undefined;
        });
        if (this.data.base?.role === USER_ROLE.READ) names.unshift(this.data.base.name);
        return [...new Set(names.filter((n): n is string => !!n))];
    }

    // makes the changes to the workspace, worked out again from it as it is now, so anything changed since is allowed for
    async applyToWorkspace() {
        this.applying = true;
        this.busy = true;
        this.error = '';
        this.step = 'working';
        this.progressText = `Changing ${this.workspaceName}…`;
        try {
            const applied = await this.modelChangeService.apply(this.data.organisation, this.workspace, this.prepared!, this.pending);
            await this.futureStateService.refreshModels(this.data.organisation);
            this.changes = [...applied, ...this.notMade];
            this.step = 'applied';
        } catch (err) {
            console.error('Unable to apply the changes', err);
            this.error = `The changes couldn't all be made, and some may have been. Check ${this.workspaceName} before trying again.`;
            this.applyFailed = true;
            this.step = 'review';
        } finally {
            this.applying = false;
            this.busy = false;
        }
    }

    // asks AI about the description, with the answers so far: it asks more, or the changes are made (or, for the
    // workspace, listed)
    private async ask() {
        this.step = 'working';
        this.progressText = 'Working out what you described with AI…';
        const project = this.target === 'workspace' ? this.workspace : this.project!;
        this.prepared ||= await this.modelChangeService.prepare(this.data.organisation, project);
        const response = await this.modelChangeService.ask(this.data.organisation, project, this.prepared, this.description.trim(), this.answers);
        if (this.cancelled) return;
        const notPossible = response.rejected.map(r => ({ decision: YOUR_REQUEST, description: r.description, applied: false, reason: r.reason }));
        if (!response.ready) {
            this.questions = (response.questions.length ? response.questions : ['Can you say more about what should change?']).map(question => ({ question, answer: '' }));
            this.step = 'questions';
            return;
        }
        if (this.target === 'workspace') {
            this.pending = [...this.pending, ...response.changes.map(c => ({ ...c, decision: YOUR_REQUEST }))];
            this.notMade = [...this.notMade, ...notPossible];
            await this.planForWorkspace();
            return;
        }
        this.progressText = 'Changing the models…';
        const applied = response.changes.length ? await this.modelChangeService.apply(this.data.organisation, this.project!, this.prepared, response.changes) : [];
        this.changes = [...this.changes, ...applied.map(c => ({ ...c, decision: YOUR_REQUEST })), ...notPossible];
        this.step = 'done';
    }

    async answer() {
        if (!this.canAnswer) return;
        this.answers = [...this.answers, ...this.questions.map(q => ({ question: q.question, answer: q.answer.trim() }))];
        this.error = '';
        try {
            await this.ask();
        } catch (err) {
            console.error('Unable to make the described changes', err);
            this.error = "AI couldn't make what you described. Please try again.";
            this.step = 'questions';
        }
    }

    // closes with the project, under the name given if it was changed, and opens it if asked
    async finish(open: boolean) {
        const name = this.name.trim();
        if (name.length < PROJECT_NAME_MIN || name.length > PROJECT_NAME_MAX) {
            this.error = `The name must be between ${PROJECT_NAME_MIN} and ${PROJECT_NAME_MAX} characters`;
            return;
        }
        this.busy = true;
        this.error = '';
        try {
            if (name !== this.project!.name) {
                await this.futureStateService.rename(this.project!, name);
                this.project!.name = name;
            }
            this.dialogRef.close({ project: this.project, open });
        } catch (err) {
            this.error = "The project couldn't be renamed. Please try again.";
        } finally {
            this.busy = false;
        }
    }

    async discard() {
        this.busy = true;
        await this.removeProject();
        this.busy = false;
        this.dialogRef.close(undefined);
    }

    // nothing has been made yet, so there's nothing to undo; any answer still coming from AI is ignored
    close() {
        this.cancelled = true;
        this.dialogRef.close(undefined);
    }

    // the project made, deleted as the user asks, or when making it fails
    private async removeProject() {
        if (!this.project) return;
        const project = this.project;
        this.project = undefined;
        this.prepared = undefined;
        await this.futureStateService.discard(project).catch(err => console.error("Unable to delete the future state's project", err));
    }
}
